import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiExternalLink, FiUsers, FiEdit2, FiClock, FiLock, FiCopy } from 'react-icons/fi';
import { toast } from '../../Toast';
import { SettingsActions } from '../../clientSettings/ClientSettings';
import { copyText } from '../../clientSettings/widgets/shared';
import { BackLink, HubHeader, hubPath, useHubSite } from '../shared';

// read-only figures: Automic sends the registry ones every evening, the ASX feed the market ones
interface Locked { label: string; value: string; source: string }

const REGISTRY: Locked[] = [
    { label: 'Fully paid ordinary shares', value: '128,400,000', source: 'Automic · synced today 6:00 pm AWST' },
    { label: 'Number of holders', value: '1,842', source: 'Automic · synced today 6:00 pm AWST' },
    { label: 'Top 20 hold', value: '42.6% of issued capital', source: 'Automic · synced today 6:00 pm AWST' },
    { label: 'Holders with less than a marketable parcel', value: '318', source: 'Automic · synced today 6:00 pm AWST' },
];

const asx = (ticker: string): Locked[] => [
    { label: `Latest share price (ASX:${ticker})`, value: 'A$0.245', source: 'ASX · today 10:42' },
    { label: 'Market capitalisation', value: 'A$31.5M', source: 'Calculated: shares on issue × price' },
    { label: '52-week range', value: 'A$0.11 – A$0.31', source: 'ASX · today 10:42' },
    { label: 'Volume (today)', value: '412,600', source: 'ASX · today 10:42' },
];

// the figures you type in
interface Form {
    options: string;
    rights: string;
    escrowed: string;
    cash: string;
    quarter: string;
    footnote: string;
}
type Key = keyof Form;

const EDITABLE: { k: Key; label: string; note?: string }[] = [
    { k: 'options', label: 'Unlisted options', note: 'Last changed 12 Aug 2026 · Appendix 3B' },
    { k: 'rights', label: 'Performance rights', note: 'Last changed 14 Feb 2026' },
    { k: 'escrowed', label: 'Escrowed shares', note: 'Released 2 Jun 2027' },
    { k: 'cash', label: 'Cash at bank (last quarterly)', note: '30 Jun 2026 · from Appendix 5B' },
    { k: 'quarter', label: 'Quarter end shown with the cash figure' },
    { k: 'footnote', label: 'Footnote shown under the table' },
];

const INITIAL: Form = { options: '4,900,000', rights: '6,670,000', escrowed: '12,000,000', cash: 'A$4.2M', quarter: '30 June 2026', footnote: 'Options exercisable at A$0.30, expiring 30 Jun 2028' };

interface Change { what: string; when: string; who: string }

const HISTORY: Change[] = [
    { what: 'Unlisted options 3.9M → 4.9M — Appendix 3B, 11 Aug', when: '12 Aug 2026', who: 'Wallace Turner' },
    { what: 'Cash at bank A$5.1M → A$4.2M — Quarterly, 30 Jun', when: '30 Jul 2026', who: 'Rob Fletcher' },
    { what: 'Ordinary shares 118.4M → 128.4M — Placement, Appendix 2A', when: '2 Jun 2026', who: 'Wallace Turner' },
    { what: 'Performance rights 5.2M → 6.67M', when: '14 Feb 2026', who: 'Rob Fletcher' },
    { what: 'Escrowed shares 0 → 12M — Vendor shares, Appendix 2A', when: '9 Dec 2025', who: 'Wallace Turner' },
    { what: 'Capital structure first added', when: '9 Sep 2025', who: 'Investi setup' },
];

const SHORTCODE = '[investi_market_data instance="capital-structure"]';

const LockedField: React.FC<{ f: Locked }> = ({ f }) =>
    <div className="field">
        <span className="label">{f.label}</span>
        <div className="value-box locked" title="Sent to Investi automatically — it can't be edited here">
            <span>{f.value}</span>
            <small className="hub-lock-tag"><FiLock /> Locked</small>
        </div>
        <small className="hub-caption">{f.source}</small>
    </div>;

const Section: React.FC<{ icon: React.ReactNode; title: string; hint: string; className?: string }> = ({ icon, title, hint, className, children }) =>
    <section className={`card${className ? ` ${className}` : ''}`}>
        <div className="section-title">
            <span className="icon-tile">{icon}</span>
            <div className="text"><h2>{title}</h2><div className="hint">{hint}</div></div>
        </div>
        {children}
    </section>;

// Website Hub › Website content › Capital structure (spec 406:10944)
const CapitalStructure: React.FC = () => {
    const history = useHistory();
    const { host, name, ticker, url } = useHubSite();
    const [form, setForm] = useState<Form>(INITIAL);
    const [saved, setSaved] = useState<Form>(INITIAL);
    const [log, setLog] = useState<Change[]>(HISTORY);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);
    const market = asx(ticker);

    const set = (k: Key, v: string) => setForm(f => ({ ...f, [k]: v }));

    // every changed figure gets a line in Update history
    const save = () => {
        const when = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        const lines = EDITABLE.filter(e => form[e.k] !== saved[e.k]).map(e => ({ what: `${e.label} ${saved[e.k] || '—'} → ${form[e.k] || '—'}`, when, who: 'You' }));
        setLog(h => [...lines, ...h]);
        setSaved(form);
        toast.info('Preview only: logged here, the figures on the website weren’t changed');
    };
    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };

    // the widget as the website shows it, from the figures on this page
    const preview = [
        ['Shares on issue', REGISTRY[0].value],
        ['Unlisted options', form.options],
        ['Performance rights', form.rights],
        ['Escrowed shares', form.escrowed],
        ['Share price', market[0].value],
        ['Market cap', market[1].value],
        ['Top 20 hold', '42.6%'],
        [`Cash at bank (${form.quarter})`, form.cash],
    ];

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="Capital Structure" sub={`Part of Website content · shares, options and market cap shown on ${host}`}>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/capital-structure')}><FiCode /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url('/investors/investors-dashboard/')} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>
            <SettingsActions {...actions} />
        </HubHeader>

        <div className="hub-cap">
            <div className="hub-cap-pair">
                <Section icon={<FiUsers />} title="From the Share Registry" hint="Automic sends these every evening — they cannot be edited here">
                    <div className="fields">{REGISTRY.map(f => <LockedField key={f.label} f={f} />)}</div>
                </Section>
                <Section icon={<FiExternalLink />} title="From the ASX Feed" hint="Price data, updated through the day on a 20-minute delay">
                    <div className="fields">{market.map(f => <LockedField key={f.label} f={f} />)}</div>
                </Section>
            </div>

            <Section icon={<FiEdit2 />} title="You Update These" hint="Everything the registry and the ASX do not send us">
                <div className="fields">
                    {EDITABLE.map(e => <label key={e.k} className="field">
                        <span className="label">{e.label}</span>
                        <input value={form[e.k]} onChange={ev => set(e.k, ev.target.value)} />
                        {e.note && <small className="hub-caption">{e.note}</small>}
                    </label>)}
                </div>
            </Section>

            <Section icon={<FiClock />} title="Update history" hint="Every change is kept so you can see who changed what">
                <div className="hub-history">
                    {log.map((c, i) => <div key={`${c.when}-${i}`}>
                        <FiEdit2 />
                        <span className="hub-history-t"><strong>{c.what}</strong><span>{c.when} · {c.who}</span></span>
                    </div>)}
                </div>
            </Section>

            {/* not in the spec PNG: how the widget looks with these figures, and its shortcode */}
            <Section icon={<FiCode />} title="Where This Shows" hint={`The Capital structure widget on ${host}, with the figures above`}>
                <div className="hub-cap-preview">
                    <div className="hub-cap-preview-h"><strong>Capital structure</strong><span>{name} · ASX: {ticker}</span></div>
                    <div className="hub-cap-preview-grid">
                        {preview.map(([l, v]) => <div key={l}><span>{l}</span><strong>{v || '—'}</strong></div>)}
                    </div>
                    {form.footnote && <p>{form.footnote}</p>}
                </div>
                <div className="hub-shortcode">
                    <code>{SHORTCODE}</code>
                    <button type="button" onClick={() => copyText(SHORTCODE, 'shortcode')}><FiCopy /> Copy</button>
                </div>
            </Section>
        </div>

        {/* mobile: Discard / Save move from the header to the end of the page (Account.scss .action-top / .action-bottom) */}
        <div className="action-bottom hub-action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default CapitalStructure;
